import React from "react";
import { Link } from "react-router-dom";
import { publicAssetUrl } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Star } from "lucide-react";

/**
 * Shared partner-shop card used both on the customer home rail and on the
 * /partner-shops list page. Whole card is a Link; a Featured ribbon shows in
 * the top-right corner when `is_featured` is true.
 */
export function PartnerShopCard({ brand, source = "partner_shops_home" }) {
    const branchCount = (brand.branches || []).length;
    const districtCount = new Set((brand.branches || []).map((b) => b.district_slug)).size;
    const initial = (brand.brand_name || "?").trim().charAt(0).toUpperCase();

    const onTap = () => {
        trackEvent("sponsored_ad_click", {
            source,
            brand_slug: brand.slug,
            target_type: "partner_shop",
            target_id: brand.slug,
        });
    };

    return (
        <Link
            to={`/partner-shops/${brand.id}`}
            onClick={onTap}
            data-testid={`partner-shop-card-${brand.slug}`}
            className="card-soft relative flex flex-col items-center gap-2 !p-4 text-center transition hover:-translate-y-0.5 active:scale-[0.99]"
            style={{ borderRadius: 20 }}
        >
            {brand.is_featured && (
                <span
                    className="absolute right-0 top-3 flex items-center gap-1 rounded-l-full bg-[var(--brand-orange)] px-2 py-1 font-heading text-[10px] font-bold uppercase tracking-wider text-white"
                    data-testid={`partner-shop-featured-ribbon-${brand.slug}`}
                >
                    <Star size={10} className="fill-white" /> Featured
                </span>
            )}

            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-[#FFF3E6] ring-1 ring-orange-100">
                {brand.logo_url ? (
                    <img
                        src={publicAssetUrl(brand.logo_url)}
                        alt={`${brand.brand_name} logo`}
                        className="h-16 w-16 object-contain"
                        onError={(e) => { e.target.style.display = "none"; }}
                    />
                ) : (
                    <span className="font-heading text-3xl font-extrabold text-[var(--brand-orange)]">{initial}</span>
                )}
            </div>

            <div className="min-w-0 self-stretch">
                <div className="line-clamp-1 font-heading text-sm font-semibold text-[var(--text-primary)] md:text-base">
                    {brand.brand_name}
                </div>
                {brand.category_slug && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-[#FFF3E6] px-2 py-0.5 font-heading text-[10px] font-semibold text-[var(--brand-orange)]">
                        {brand.category_slug.replace(/-/g, " ")}
                    </span>
                )}
                <div className="mt-1 font-body text-[10px] text-[var(--text-muted)]">
                    {branchCount} branch{branchCount === 1 ? "" : "es"} · {districtCount} district{districtCount === 1 ? "" : "s"}
                </div>
            </div>
        </Link>
    );
}

export default PartnerShopCard;
